<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <div class="tw-col-span-12 lg:tw-col-span-12">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 v-if="!loading" class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Contacts</h1>
                        <div v-else class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-1 tw-animate-pulse"></div>
                        
                        <p v-if="!loading" class="tw-text-sm tw-text-gray-600">{{ contacts.length }} contacts total</p>
                        <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                    </div>
                    
                    <!-- Header Actions -->
                    <div v-if="!loading" class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search contacts..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'grid' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:widget-4-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Export Button -->
                        <a-button
                            @click="exportContacts"
                            intent="secondary"
                            left-icon="solar:export-bold-duotone"
                        >
                            Export
                        </a-button>
                        
                    </div>
                    
                    <!-- Header Actions Skeleton -->
                    <div v-else class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-64 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-20 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-28 tw-animate-pulse"></div>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Online Filter -->
                    <FormField
                        v-model="selectedStatus"
                        type="select"
                        placeholder="All contacts"
                        left-icon="solar:user-check-bold-duotone"
                        hide-label
                        hide-errors
                        class="tw-min-w-[150px]"
                    >
                        <option value="">All Contacts</option>
                        <option value="active">Online Only</option>
                    </FormField>

                    <!-- Sort -->
                    <FormField
                        v-model="sortBy"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:sort-horizontal-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="company">Sort by Company</option>
                        <option value="status">Sort by Status</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>

                    <!-- Bulk Actions -->
                    <div v-if="selectedContacts.length > 0" class="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                        <span class="tw-text-sm tw-text-gray-600">{{ selectedContacts.length }} selected</span>
                        <a-button
                            @click="exportSelected"
                            intent="secondary"
                            left-icon="solar:export-bold-duotone"
                            class="tw-text-sm"
                        >
                            Export
                        </a-button>
                        <a-button
                            @click="messageSelected"
                            intent="primary"
                            left-icon="solar:chat-round-bold-duotone"
                            class="tw-text-sm"
                        >
                            Message
                        </a-button>
                        <a-button
                            @click="removeSelected"
                            intent="danger"
                            left-icon="solar:trash-bin-2-bold-duotone"
                            class="tw-text-sm"
                        >
                            Remove
                        </a-button>
                    </div>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Grid View Skeleton -->
                        <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                            <div
                                v-for="n in 8"
                                :key="n"
                                class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-animate-pulse"
                            >
                                <!-- Skeleton Checkbox and Actions -->
                                <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                    <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                    </div>
                                </div>

                                <!-- Skeleton Avatar -->
                                <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-full tw-mx-auto tw-mb-3"></div>

                                <!-- Skeleton Contact Info -->
                                <div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4 tw-mx-auto"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-full"></div>
                                    <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16 tw-mx-auto tw-mt-2"></div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-px-4 tw-py-3 tw-text-left">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Contact</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Company</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Email</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr
                                        v-for="n in 12"
                                        :key="n"
                                        class="tw-animate-pulse"
                                    >
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-items-center">
                                                <div class="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded-full tw-mr-3"></div>
                                                <div>
                                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-1"></div>
                                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-40"></div>
                                        </td>

                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="filteredContacts.length === 0 && !searchQuery && !selectedStatus" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:users-group-two-rounded-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No contacts yet</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start connecting with other users and they'll appear here</p>
                        <a-button
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Find People
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="filteredContacts.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:magnifer-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No contacts found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Grid View -->
                    <div v-else-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                        <div
                            v-for="contact in paginatedContacts"
                            :key="contact.id"
                            class="tw-group tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer"
                            @click="selectContact(contact)"
                        >
                            <!-- Selection Checkbox -->
                            <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                <input
                                    type="checkbox"
                                    :checked="selectedContacts.includes(contact.id)"
                                    @click.stop="toggleContactSelection(contact.id)"
                                    class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                />
                                
                                <!-- Card Actions -->
                                <div class="tw-flex tw-items-center tw-gap-1">
                                    <button
                                        @click.stop="viewProfile(contact)"
                                        class="tw-p-1.5 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100"
                                        title="View Profile"
                                    >
                                        <icon icon="solar:user-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <button
                                        @click.stop="sendMessage(contact)"
                                        class="tw-p-1.5 tw-rounded-md tw-text-gray-400 hover:tw-text-blue-600 hover:tw-bg-blue-50"
                                        title="Send Message"
                                    >
                                        <icon icon="solar:chat-round-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <button
                                        @click.stop="removeContact(contact)"
                                        class="tw-p-1.5 tw-rounded-md tw-text-gray-400 hover:tw-text-red-600 hover:tw-bg-red-50"
                                        title="Remove Contact"
                                    >
                                        <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                    </button>
                                </div>
                            </div>

                            <!-- Avatar -->
                            <div class="tw-flex tw-justify-center tw-mb-3">
                                <div class="tw-relative">
                                    <img
                                        v-if="contact.avatar"
                                        :src="contact.avatar"
                                        :alt="contact.name"
                                        class="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
                                    />
                                    <div
                                        v-else
                                        class="tw-w-16 tw-h-16 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-100"
                                    >
                                        <icon icon="solar:user-bold-duotone" class="tw-text-2xl tw-text-primary-500" />
                                    </div>
                                    
                                    <!-- Online Status Indicator -->
                                    <div
                                        v-if="contact.status === 'active'"
                                        class="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-rounded-full tw-border-2 tw-border-white tw-bg-green-500"
                                    ></div>
                                </div>
                            </div>

                            <!-- Contact Info -->
                            <div class="tw-text-center">
                                <h3 class="tw-font-semibold tw-text-gray-900 tw-mb-1 tw-truncate" :title="contact.name">
                                    {{ contact.name }}
                                </h3>
                                <p class="tw-text-sm tw-text-gray-600 tw-mb-1 tw-truncate" :title="contact.position + ' at ' + contact.company">
                                    {{ contact.position }}
                                </p>
                                <p class="tw-text-sm tw-text-gray-500 tw-mb-2 tw-truncate" :title="contact.company">
                                    {{ contact.company }}
                                </p>
                                
                
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-px-4 tw-py-3 tw-text-left">
                                        <input
                                            type="checkbox"
                                            :checked="allContactsSelected"
                                            @change="toggleAllContacts"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Contact</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Company</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Email</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="contact in paginatedContacts"
                                    :key="contact.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-4 tw-py-4">
                                        <input
                                            type="checkbox"
                                            :checked="selectedContacts.includes(contact.id)"
                                            @change="toggleContactSelection(contact.id)"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-flex tw-items-center">
                                            <div class="tw-relative tw-mr-3">
                                                <img
                                                    v-if="contact.avatar"
                                                    :src="contact.avatar"
                                                    :alt="contact.name"
                                                    class="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
                                                />
                                                <div
                                                    v-else
                                                    class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center"
                                                >
                                                    <icon icon="solar:user-bold-duotone" class="tw-text-lg tw-text-primary-500" />
                                                </div>
                                                
                                                <!-- Online Status Indicator -->
                                                <div
                                                    v-if="contact.status === 'active'"
                                                    class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-rounded-full tw-border tw-border-white tw-bg-green-500"
                                                ></div>
                                            </div>
                                            <div>
                                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ contact.name }}</div>
                                                <div class="tw-text-sm tw-text-gray-500">{{ contact.position }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-900">{{ contact.company }}</td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-500">{{ contact.email }}</td>
                                    <td class="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-medium">
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                            <button
                                                @click="viewProfile(contact)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="View Profile"
                                            >
                                                <icon icon="solar:user-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <button
                                                @click="sendMessage(contact)"
                                                class="tw-text-gray-400 hover:tw-text-blue-600"
                                                title="Send Message"
                                            >
                                                <icon icon="solar:chat-round-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <button
                                                @click="removeContact(contact)"
                                                class="tw-text-gray-400 hover:tw-text-red-600"
                                                title="Remove Contact"
                                            >
                                                <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredContacts.length) }} of {{ filteredContacts.length }} contacts
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="currentPage--"
                                :disabled="currentPage === 1"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="currentPage++"
                                :disabled="currentPage === totalPages"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingContact"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            :title="deleteModalTitle"
            :description="deleteModalDescription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import FormField from '@/components/form/FormField.vue'
import useAlert from '@/composables/useAlert'

// Data
const loading = ref(false)
const searchQuery = ref('')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const selectedContacts = ref([])
const selectedStatus = ref('')
const deleteModalVisible = ref(false)
const deletingContact = ref(false)
const contactToDelete = ref(null)

// Sort and filters
const sortBy = ref('name')

// Sample contacts data
const contacts = ref([
    {
        id: 1,
        name: 'Sarah Johnson',
        email: 'sarah.johnson@techcorp.com',
        avatar: '/assets/images/avatars/avatar.png',
        company: 'TechCorp Solutions',
        position: 'Senior Product Manager',
        connectedDate: new Date('2024-01-15'),
        status: 'active',
        lastInteraction: new Date('2024-01-20')
    },
    {
        id: 2,
        name: 'Michael Chen',
        email: 'michael.chen@innovate.io',
        avatar: '',
        company: 'Innovate Labs',
        position: 'Lead Developer',
        connectedDate: new Date('2024-01-10'),
        status: 'active',
        lastInteraction: new Date('2024-01-19')
    },
    {
        id: 3,
        name: 'Emily Davis',
        email: 'emily.davis@startup.com',
        avatar: '/assets/images/avatars/avatar.png',
        company: 'StartupCo',
        position: 'Marketing Director',
        connectedDate: new Date('2024-01-08'),
        status: 'pending',
        lastInteraction: new Date('2024-01-12')
    },
    {
        id: 4,
        name: 'David Wilson',
        email: 'david.wilson@enterprise.org',
        avatar: '',
        company: 'Enterprise Solutions',
        position: 'Business Analyst',
        connectedDate: new Date('2024-01-05'),
        status: 'active',
        lastInteraction: new Date('2024-01-18')
    },
    {
        id: 5,
        name: 'Lisa Thompson',
        email: 'lisa.thompson@consulting.com',
        avatar: '/assets/images/avatars/avatar.png',
        company: 'Thompson Consulting',
        position: 'Senior Consultant',
        connectedDate: new Date('2024-01-12'),
        status: 'blocked',
        lastInteraction: new Date('2024-01-14')
    },
    {
        id: 6,
        name: 'James Rodriguez',
        email: 'james.rodriguez@media.net',
        avatar: '',
        company: 'Digital Media Inc',
        position: 'Creative Director',
        connectedDate: new Date('2024-01-18'),
        status: 'active',
        lastInteraction: new Date('2024-01-21')
    },
    {
        id: 7,
        name: 'Amanda Foster',
        email: 'amanda.foster@finance.co',
        avatar: '/assets/images/avatars/avatar.png',
        company: 'Foster Financial',
        position: 'Financial Advisor',
        connectedDate: new Date('2024-01-14'),
        status: 'pending',
        lastInteraction: new Date('2024-01-16')
    },
    {
        id: 8,
        name: 'Robert Kim',
        email: 'robert.kim@healthcare.org',
        avatar: '',
        company: 'HealthTech Systems',
        position: 'Technical Lead',
        connectedDate: new Date('2024-01-20'),
        status: 'active',
        lastInteraction: new Date('2024-01-22')
    }
])

// Computed properties
const hasActiveFilters = computed(() => {
    return selectedStatus.value || searchQuery.value
})

const filteredContacts = computed(() => {
    let filtered = contacts.value

    // Search filter
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(contact =>
            contact.name.toLowerCase().includes(query) ||
            contact.email.toLowerCase().includes(query) ||
            contact.company.toLowerCase().includes(query) ||
            contact.position.toLowerCase().includes(query)
        )
    }

    // Status filter
    if (selectedStatus.value) {
        filtered = filtered.filter(contact => contact.status === selectedStatus.value)
    }

    // Sort
    filtered.sort((a, b) => {
        switch (sortBy.value) {
            case 'name':
                return a.name.localeCompare(b.name)
            case 'company':
                return a.company.localeCompare(b.company)
            case 'connected':
                return new Date(b.connectedDate) - new Date(a.connectedDate)
            case 'interaction':
                return new Date(b.lastInteraction) - new Date(a.lastInteraction)
            default:
                return 0
        }
    })

    return filtered
})

const totalPages = computed(() => {
    return Math.ceil(filteredContacts.value.length / itemsPerPage.value)
})

const paginatedContacts = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return filteredContacts.value.slice(start, end)
})

const allContactsSelected = computed(() => {
    return paginatedContacts.value.length > 0 && 
           paginatedContacts.value.every(contact => selectedContacts.value.includes(contact.id))
})

const deleteModalTitle = computed(() => {
    if (selectedContacts.value.length > 1) {
        return `Remove ${selectedContacts.value.length} contacts?`
    }
    return 'Remove contact?'
})

const deleteModalDescription = computed(() => {
    if (selectedContacts.value.length > 1) {
        return 'This action cannot be undone. All selected contacts will be removed from your contact list.'
    }
    return 'This action cannot be undone. The contact will be removed from your contact list.'
})

// Methods
const clearFilters = () => {
    selectedStatus.value = ''
    searchQuery.value = ''
    currentPage.value = 1
}

const toggleContactSelection = (contactId) => {
    const index = selectedContacts.value.indexOf(contactId)
    if (index > -1) {
        selectedContacts.value.splice(index, 1)
    } else {
        selectedContacts.value.push(contactId)
    }
}

const toggleAllContacts = () => {
    if (allContactsSelected.value) {
        selectedContacts.value = selectedContacts.value.filter(id =>
            !paginatedContacts.value.some(contact => contact.id === id)
        )
    } else {
        paginatedContacts.value.forEach(contact => {
            if (!selectedContacts.value.includes(contact.id)) {
                selectedContacts.value.push(contact.id)
            }
        })
    }
}

const selectContact = (contact) => {
    viewProfile(contact)
}

const viewProfile = (contact) => {
    console.log('Viewing profile:', contact.name)
    useAlert(`Opening profile for ${contact.name}`, 'info')
}

const sendMessage = (contact) => {
    console.log('Sending message to:', contact.name)
    useAlert(`Message dialog opened for ${contact.name}`, 'info')
}

const removeContact = (contact) => {
    contactToDelete.value = contact
    selectedContacts.value = [contact.id]
    deleteModalVisible.value = true
}

const exportContacts = () => {
    console.log('Exporting all contacts')
    useAlert('Contact export started. You will receive a download link shortly.', 'success')
}

const exportSelected = () => {
    console.log('Exporting selected contacts:', selectedContacts.value)
    useAlert(`Exporting ${selectedContacts.value.length} selected contacts`, 'success')
}

const messageSelected = () => {
    console.log('Sending message to selected contacts:', selectedContacts.value)
    useAlert(`Opening message dialog for ${selectedContacts.value.length} contacts`, 'info')
}

const removeSelected = () => {
    deleteModalVisible.value = true
}

const confirmDelete = () => {
    deletingContact.value = true
    
    // Simulate delete operation
    setTimeout(() => {
        contacts.value = contacts.value.filter(contact => 
            !selectedContacts.value.includes(contact.id)
        )
        selectedContacts.value = []
        deletingContact.value = false
        deleteModalVisible.value = false
        contactToDelete.value = null
        useAlert('Contact(s) removed successfully', 'success')
    }, 1000)
}



const loadData = async () => {
    loading.value = true

    try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
    } catch (error) {
        console.error('Error loading data:', error)
        useAlert('Failed to load contacts', 'danger')
    } finally {
        loading.value = false
    }
}

// Initialize
onMounted(() => {
    loadData()
})
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>